import * as Sentry from '@sentry/vue'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import { toast } from 'vue-sonner'
import App from '@/App.vue'
import { vAutofocus } from '@/directives/autofocus'
import { i18n } from '@/i18n'
import { router } from '@/router'
import { impersonationReturnRoute } from '@/services/impersonation'
import { setupNetworkToast, NETWORK_DEGRADED_TOAST_ID } from '@/services/network/network_toast'
import { onImpersonationEnded, onSessionExpired } from '@/services/network/session_events'
import { setSentryUser } from '@/services/sentry/user'
import { readLastUserHint, useAuthStore } from '@/stores/auth'
import '@/composables/useHaptic'
import '@/styles/application.css'

// ESLint projectService не резолвит типы .vue SFC (vue-tsc резолвит и
// проверяет полноценно) — единственное место, глушим точечно.
// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const app = createApp(App)

// Глобальная фокус-политика: десктоп — автофокус первого поля, тач — никогда.
app.directive('autofocus', vAutofocus)

const sentryDsn = document.querySelector('meta[name="sentry-dsn"]')?.getAttribute('content')
if (sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    environment: document.querySelector('meta[name="sentry-environment"]')?.getAttribute('content') ?? undefined,
    release: document.querySelector('meta[name="sentry-release"]')?.getAttribute('content') ?? undefined,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
  })

  // Ранний user-context из localStorage — до /auth/me. Если /me зависнет
  // или упадёт, Sentry уже знает кто юзер по последнему успешному login.
  // После fetchMe контекст перезапишется свежими данными
  // (см. stores/auth.ts → applySession → setSentryUser).
  const hint = readLastUserHint()
  if (hint) setSentryUser(hint)
}

// Необработанные Vue-ошибки (render, lifecycle hooks, watcher'ы) по умолчанию
// только логируются в консоль и в Sentry НЕ попадают — ставим errorHandler
// руками. Регистрируем ВСЕГДА, не только при наличии Sentry DSN: на стенде без
// DSN (staging / локальный прод-билд) Sentry.captureException — безопасный no-op,
// а console.error оставляет ошибку видимой. Без безусловной регистрации
// render-ошибки на таком стенде терялись бы молча (M-fe9).
app.config.errorHandler = (err, _instance, info) => {
  Sentry.captureException(err, { extra: { info } })
  console.error(err)
}

// Необработанные rejection'ы промисов (fire-and-forget `void fn()` без .catch,
// сетевой throw из useApiRequest вне submit-обработчика) по умолчанию не попадают
// ни в Vue errorHandler, ни в Sentry. Ловим глобально, чтобы они были видны в
// мониторинге и dev-консоли, а не «исчезали» (M-fe10).
window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason)
  console.error(event.reason)
})

app.use(createPinia())
app.use(router)
app.use(i18n)

// Сессия окончательно потеряна (refresh не помог, см. services/network/with_refresh):
// чистим стор и уводим на /login. При первичной загрузке (auth ещё не initialized)
// редирект делает router.beforeEach — здесь его не дублируем.
onSessionExpired(() => {
  const auth = useAuthStore()
  const wasInitialized = auth.initialized
  auth.markLoggedOut()
  if (!wasInitialized) return

  const current = router.currentRoute.value
  if (current.name !== 'login') {
    void router.replace({ name: 'login', query: { next: current.fullPath } })
  }
})

// Имперсонация оборвалась (E18, §11): интерсептор уже вернул админа через refresh.
// Синхронизируем стор (→ админ, баннер исчезает), показываем тост и уводим туда же,
// куда и кнопка «Вернуться к себе» — в карточку сотрудника. Сессия самого админа
// жива, на /login не уводим.
onImpersonationEnded(() => {
  const auth = useAuthStore()
  const employeeId = auth.user?.id
  void auth.applyImpersonationEnded().then(() => {
    toast(i18n.global.t('impersonation.ended'))
    void router.push(impersonationReturnRoute(employeeId, auth.capabilities))
  })
})

// Сеть деградировала при живом соединении (ttfb/overall/network из smartFetch):
// API таймаутит/тихо дропается, юзер мог бы долго работать «в пустоту». Показываем
// один залипший глобальный тост (id фиксирован → sonner схлопывает дубли) и снимаем
// его при восстановлении связи: первый успешный ответ или браузерное событие `online`.
// Лайфцикл — в network_toast (тестируемо), здесь только проводка к sonner/i18n/window.
setupNetworkToast({
  show: () =>
    toast.error(i18n.global.t('network.degraded.title'), {
      id: NETWORK_DEGRADED_TOAST_ID,
      description: i18n.global.t('network.degraded.hint'),
      duration: Infinity,
      // Снимается только при восстановлении связи — это его смысл. Без этого
      // свайп-дисмисс убрал бы тост из DOM, оставив shown=true в setupNetworkToast,
      // и индикатор не переоткрылся бы при длящейся деградации (повторный degraded
      // уходит в no-op).
      dismissible: false,
    }),
  dismiss: () => toast.dismiss(NETWORK_DEGRADED_TOAST_ID),
  onOnline: (handler) => window.addEventListener('online', handler),
})

app.mount('#app')

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js', { scope: '/' }).catch(() => {})
  })

  navigator.serviceWorker.addEventListener('message', (e) => {
    const msg = e.data as { type?: string; path?: string } | null
    if (!msg) return

    // Клик по push-уведомлению (§4.7): SW сфокусировал окно и прислал deep-link
    // на связанную сущность. Навигируем роутером — без перезагрузки, работает и
    // в установленной PWA, где окно одно. Принимаем только внутренние пути.
    if (msg.type === 'push_action' && typeof msg.path === 'string' && msg.path.startsWith('/') && !msg.path.startsWith('//')) {
      void router.push(msg.path)
    }
  })
}
